import { useState, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Plus, Trash2, Search, RefreshCw, Shield, Edit } from "lucide-react";
import { notify } from "@/lib/utils/toast";
import { awardsApi, Award as ApiAward, AwardFilters } from "../lib/api/awards";
import { useAuthStore, PERMISSIONS } from "../lib/stores/auth";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { PaginationIndex } from "./PaginationIndex";
// Map API Award to display format
interface AwardDisplay extends ApiAward {
	// Add any display-specific properties if needed
}

export default function AwardManagement() {
	const { hasPermission } = useAuthStore();
	const [awards, setAwards] = useState<AwardDisplay[]>([]);
	const [loading, setLoading] = useState(true);
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [editingAward, setEditingAward] = useState<AwardDisplay | null>(null);
	const [searchQuery, setSearchQuery] = useState("");
	const [awardTypeFilter, setAwardTypeFilter] = useState("all");
	const [levelFilter, setLevelFilter] = useState("all");
	const [dateFromFilter, setDateFromFilter] = useState("");
	const [dateToFilter, setDateToFilter] = useState("");
	const [deleteItemId, setDeleteItemId] = useState<string | null>(null);

	// Pagination
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [totalCount, setTotalCount] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState(10);

	// Permission checks
	const canView = hasPermission(PERMISSIONS.awards.permissions.view.key);
	const canCreate = hasPermission(PERMISSIONS.awards.permissions.create.key);
	const canUpdate = hasPermission(PERMISSIONS.awards.permissions.update.key);
	const canDelete = hasPermission(PERMISSIONS.awards.permissions.delete.key);
	const canImport = hasPermission(PERMISSIONS.awards.permissions.import.key);
	const canExport = hasPermission(PERMISSIONS.awards.permissions.export.key);

	// Show permission denied if user doesn't have view permission
	if (!canView) {
		return (
			<div className='flex items-center justify-center h-96'>
				<div className='text-center'>
					<Shield className='h-16 w-16 mx-auto text-muted-foreground mb-4' />
					<h3 className='text-lg font-medium mb-2'>Không có quyền truy cập</h3>
					<p className='text-muted-foreground'>Bạn không có quyền xem danh sách khen thưởng.</p>
				</div>
			</div>
		);
	}

	const [formData, setFormData] = useState({
		studentName: "",
		cccd: "",
		studentId: "",
		dateOfBirth: "",
		placeOfBirth: "",
		gender: "Nam",
		ethnicCode: "01",
		ethnicity: "Kinh",
		nationalityCode: "VN",
		nationality: "Việt Nam",
		awardNumber: "",
		awardType: "Học sinh giỏi",
		awardTypeId: "01",
		title: "",
		level: "Trường",
		levelId: "03",
		awardDate: "",
		awardedBy: "",
		awardedByCode: "",
		reason: "",
		academicYear: "",
		semester: "",
		subject: "",
		competition: "",
		rank: "",
		signedBy: "",
		signerTitle: "",
		validFrom: "",
		validTo: "",
		notes: "",
		status: "valid" as const,
	});

	// Load awards data
	const loadAwards = async () => {
		try {
			setLoading(true);
			const filters: AwardFilters = {
				search: searchQuery || undefined,
				awardType: awardTypeFilter !== "all" ? awardTypeFilter : undefined,
				level: levelFilter !== "all" ? levelFilter : undefined,
				awardDateFrom: dateFromFilter || undefined,
				awardDateTo: dateToFilter || undefined,
				page: currentPage,
				limit: itemsPerPage,
			};

			const response = await awardsApi.getAll(filters);
			if (response.success && response.data) {
				setAwards(response.data.data);
				setTotalPages(response.data.totalPages);
				setTotalCount(response.data.total);
			}
		} catch (error) {
			console.error("Error loading awards:", error);
			notify.error("Lỗi khi tải dữ liệu khen thưởng");
		} finally {
			setLoading(false);
		}
	};

	// Load data when component mounts or filters change
	const timer = useRef<any>(null);
	useEffect(() => {
		if (timer.current) clearTimeout(timer.current);
		timer.current = setTimeout(() => {
			loadAwards();
		}, 1000);
	}, [itemsPerPage, currentPage, searchQuery, awardTypeFilter, levelFilter, dateFromFilter, dateToFilter]);

	const handleImport = async (data: any[]) => {
		if (!canImport) {
			notify.error("Bạn không có quyền import khen thưởng");
			throw new Error("Không có quyền import");
		}

		try {
			const importData = data.map((row) => ({
				studentName: row.studentName || row.recipientName || "",
				cccd: row.cccd || "",
				studentId: row.studentId || "",
				dateOfBirth: row.dateOfBirth || "",
				placeOfBirth: row.placeOfBirth || "",
				gender: row.gender || "Nam",
				ethnicCode: row.ethnicCode || "01",
				ethnicity: row.ethnicity || "Kinh",
				nationalityCode: row.nationalityCode || "VN",
				nationality: row.nationality || "Việt Nam",
				awardNumber: row.awardNumber || "",
				awardType: row.awardType || "Học sinh giỏi",
				awardTypeId: row.awardTypeId || "01",
				title: row.title || row.awardName || "",
				level: row.level || "Trường",
				levelId: row.levelId || "03",
				awardDate: row.awardDate || row.issuedDate || "",
				awardedBy: row.awardedBy || row.institution || "",
				awardedByCode: row.awardedByCode || "",
				reason: row.reason || row.achievementDetails || "",
				academicYear: row.academicYear || "",
				semester: row.semester || "",
				subject: row.subject || "",
				competition: row.competition || "",
				rank: row.rank || "",
				signedBy: row.signedBy || row.issuerSignature || "",
				signerTitle: row.signerTitle || "",
				validFrom: row.validFrom || row.awardDate || row.issuedDate || "",
				validTo: row.validTo || "",
				notes: row.notes || row.description || "",
				status: row.status === "revoked" || row.status === "expired" ? row.status : "valid",
			}));

			const response = await awardsApi.bulkImport(importData);
			if (response.success) {
				notify.success(response.message || `Import thành công ${response.data?.count || 0} khen thưởng`);
				await loadAwards(); // Reload data
			} else {
				throw new Error(response.message || "Lỗi import dữ liệu");
			}
		} catch (error: any) {
			throw new Error(error.message || "Lỗi xử lý dữ liệu import");
		}
	};

	const handleExport = async () => {
		if (!canExport) {
			notify.error("Bạn không có quyền export khen thưởng");
			return [];
		}

		try {
			const filters: AwardFilters = {
				search: searchQuery || undefined,
				awardType: awardTypeFilter !== "all" ? awardTypeFilter : undefined,
				level: levelFilter !== "all" ? levelFilter : undefined,
				awardDateFrom: dateFromFilter || undefined,
				awardDateTo: dateToFilter || undefined,
			};

			const response = await awardsApi.export(filters);
			if (response.success && response.data) {
				return response.data.map((award) => ({
					studentName: award.studentName,
					cccd: award.cccd,
					studentId: award.studentId || "",
					dateOfBirth: award.dateOfBirth,
					placeOfBirth: award.placeOfBirth,
					gender: award.gender,
					ethnicity: award.ethnicity,
					nationality: award.nationality,
					awardNumber: award.awardNumber,
					awardType: award.awardType,
					title: award.title,
					level: award.level,
					awardDate: award.awardDate,
					awardedBy: award.awardedBy,
					reason: award.reason,
					academicYear: award.academicYear || "",
					semester: award.semester || "",
					subject: award.subject || "",
					competition: award.competition || "",
					rank: award.rank || "",
					signedBy: award.signedBy,
					signerTitle: award.signerTitle,
					validFrom: award.validFrom,
					validTo: award.validTo || "",
					notes: award.notes || "",
					status: award.status,
				}));
			}
			return [];
		} catch (error) {
			notify.error("Lỗi xuất dữ liệu");
			return [];
		}
	};

	const generateAwardTemplate = () => [
		{
			studentName: "Nguyễn Văn A",
			cccd: "012345678901",
			studentId: "SV2024001",
			dateOfBirth: "2001-01-01",
			placeOfBirth: "Hà Nội",
			gender: "Nam",
			ethnicity: "Kinh",
			nationality: "Việt Nam",
			awardNumber: "QD-001/2024",
			awardType: "Học sinh giỏi",
			title: "Học sinh giỏi cấp trường",
			level: "Trường",
			awardDate: "2024-06-15",
			awardedBy: "Trường Đại học ABC",
			reason: "Đạt điểm trung bình 9.0",
			academicYear: "2023-2024",
			semester: "Cả năm",
			signedBy: "PGS. Nguyễn Văn B",
			signerTitle: "Hiệu trưởng",
			validFrom: "2024-06-15",
			notes: "Ghi chú thêm",
			status: "valid",
		},
	];

	// Reset to first page when filters change
	const handleFilterChange = (setter: Function, value: any) => {
		setter(value);
		setCurrentPage(1);
	};

	const resetForm = () => {
		setFormData({
			studentName: "",
			cccd: "",
			studentId: "",
			dateOfBirth: "",
			placeOfBirth: "",
			gender: "Nam",
			ethnicCode: "01",
			ethnicity: "Kinh",
			nationalityCode: "VN",
			nationality: "Việt Nam",
			awardNumber: "",
			awardType: "Học sinh giỏi",
			awardTypeId: "01",
			title: "",
			level: "Trường",
			levelId: "03",
			awardDate: "",
			awardedBy: "",
			awardedByCode: "",
			reason: "",
			academicYear: "",
			semester: "",
			subject: "",
			competition: "",
			rank: "",
			signedBy: "",
			signerTitle: "",
			validFrom: "",
			validTo: "",
			notes: "",
			status: "valid",
		});
		setEditingAward(null);
	};

	const handleEdit = (award: AwardDisplay) => {
		if (!canUpdate) {
			notify.error("Bạn không có quyền chỉnh sửa khen thưởng");
			return;
		}

		setEditingAward(award);
		setFormData({
			studentName: award.studentName,
			cccd: award.cccd,
			studentId: award.studentId || "",
			dateOfBirth: award.dateOfBirth,
			placeOfBirth: award.placeOfBirth,
			gender: award.gender,
			ethnicCode: award.ethnicCode,
			ethnicity: award.ethnicity,
			nationalityCode: award.nationalityCode,
			nationality: award.nationality,
			awardNumber: award.awardNumber,
			awardType: award.awardType,
			awardTypeId: award.awardTypeId,
			title: award.title,
			level: award.level,
			levelId: award.levelId,
			awardDate: award.awardDate,
			awardedBy: award.awardedBy,
			awardedByCode: award.awardedByCode,
			reason: award.reason,
			academicYear: award.academicYear || "",
			semester: award.semester || "",
			subject: award.subject || "",
			competition: award.competition || "",
			rank: award.rank || "",
			signedBy: award.signedBy,
			signerTitle: award.signerTitle,
			validFrom: award.validFrom,
			validTo: award.validTo || "",
			notes: award.notes || "",
			status: award.status,
		});
		setIsDialogOpen(true);
	};

	const handleDelete = async () => {
		if (!canDelete) {
			notify.error("Bạn không có quyền xóa khen thưởng");
			return;
		}
		if (deleteItemId == null) {
			notify.error("Chưa chọn văn bằng cần xóa");
			return;
		}

		try {
			const response = await awardsApi.delete(deleteItemId);
			notify.success("Đã xóa khen thưởng thành công");
			await loadAwards();
		} catch (error) {
			notify.error("Lỗi khi xóa khen thưởng");
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (editingAward && !canUpdate) {
			notify.error("Bạn không có quyền cập nhật khen thưởng");
			return;
		}

		if (!editingAward && !canCreate) {
			notify.error("Bạn không có quyền tạo khen thưởng");
			return;
		}

		try {
			if (editingAward) {
				const response = await awardsApi.update(editingAward.id, formData);
				if (response.success) {
					notify.success("Đã cập nhật khen thưởng thành công");
				} else {
					notify.error(response.message || "Lỗi khi cập nhật khen thưởng");
				}
			} else {
				const response = await awardsApi.create(formData);
				if (response.success) {
					notify.success("Đã thêm khen thưởng mới thành công");
				} else {
					notify.error(response.message || "Lỗi khi thêm khen thưởng");
				}
			}

			setIsDialogOpen(false);
			resetForm();
			await loadAwards(); // Reload data
		} catch (error) {
			notify.error("Lỗi khi lưu dữ liệu");
		}
	};

	const getStatusLabel = (status: string) => {
		switch (status) {
			case "valid":
				return "Có hiệu lực";
			case "revoked":
				return "Đã thu hồi";
			case "expired":
				return "Hết hạn";
			default:
				return status;
		}
	};

	const getStatusVariant = (status: string) => {
		switch (status) {
			case "valid":
				return "default";
			case "revoked":
				return "destructive";
			case "expired":
				return "secondary";
			default:
				return "outline";
		}
	};

	const clearFilters = () => {
		setAwardTypeFilter("all");
		setLevelFilter("all");
		setDateFromFilter("");
		setDateToFilter("");
	};

	return (
		<div className='space-y-6'>
			<div className='flex items-center justify-between'>
				<div>
					<h2 className='text-2xl mb-2'>Quản lý Khen thưởng</h2>
					{/* <p className='text-muted-foreground'>Quản lý giấy khen, bằng khen, danh hiệu thi đua</p> */}
				</div>
				<div className='flex gap-2'>
					<Button variant='outline' onClick={loadAwards} disabled={loading}>
						<RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
						Làm mới
					</Button>
					{canCreate && (
						<Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
							<DialogTrigger asChild>
								<Button variant={"add"} onClick={resetForm}>
									<Plus className='w-4 h-4' />
									Thêm khen thưởng
								</Button>
							</DialogTrigger>
							<DialogContent className='max-w-4xl max-h-[90vh] overflow-y-auto'>
								<DialogHeader>
									<DialogTitle>{editingAward ? "Cập nhật" : "Thêm mới"} khen thưởng</DialogTitle>
									{/* <DialogDescription>{editingAward ? "Cập nhật thông tin khen thưởng" : "Thêm thông tin khen thưởng mới vào hệ thống"}</DialogDescription> */}
								</DialogHeader>
								<form onSubmit={handleSubmit} className='space-y-4'>
									<div className='grid grid-cols-2 gap-4'>
										<div className='space-y-2'>
											<Label htmlFor='studentName'>Họ tên học sinh</Label>
											<Input
												id='studentName'
												value={formData.studentName}
												onChange={(e) =>
													setFormData((prev) => ({
														...prev,
														studentName: e.target.value,
													}))
												}
												placeholder='Nhập họ tên đầy đủ'
												required
											/>
										</div>
										<div className='space-y-2'>
											<Label htmlFor='cccd'>Số CCCD</Label>
											<Input
												id='cccd'
												value={formData.cccd}
												onChange={(e) =>
													setFormData((prev) => ({
														...prev,
														cccd: e.target.value,
													}))
												}
												placeholder='Nhập số CCCD'
												required
											/>
										</div>
										<div className='space-y-2'>
											<Label htmlFor='studentId'>Mã học sinh</Label>
											<Input
												id='studentId'
												value={formData.studentId}
												onChange={(e) =>
													setFormData((prev) => ({
														...prev,
														studentId: e.target.value,
													}))
												}
												placeholder='Nhập mã học sinh (nếu có)'
											/>
										</div>
										<div className='space-y-2'>
											<Label htmlFor='dateOfBirth'>Ngày sinh</Label>
											<Input
												id='dateOfBirth'
												type='date'
												value={formData.dateOfBirth}
												onChange={(e) =>
													setFormData((prev) => ({
														...prev,
														dateOfBirth: e.target.value,
													}))
												}
												required
											/>
										</div>
										<div className='space-y-2'>
											<Label htmlFor='placeOfBirth'>Nơi sinh</Label>
											<Input
												id='placeOfBirth'
												value={formData.placeOfBirth}
												onChange={(e) =>
													setFormData((prev) => ({
														...prev,
														placeOfBirth: e.target.value,
													}))
												}
												placeholder='VD: Hà Nội'
												required
											/>
										</div>
										<div className='space-y-2'>
											<Label htmlFor='gender'>Giới tính</Label>
											<Select
												value={formData.gender}
												onValueChange={(value) =>
													setFormData((prev) => ({
														...prev,
														gender: value,
													}))
												}
											>
												<SelectTrigger>
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value='Nam'>Nam</SelectItem>
													<SelectItem value='Nữ'>Nữ</SelectItem>
												</SelectContent>
											</Select>
										</div>
										<div className='space-y-2'>
											<Label htmlFor='awardNumber'>Số khen thưởng</Label>
											<Input
												id='awardNumber'
												value={formData.awardNumber}
												onChange={(e) =>
													setFormData((prev) => ({
														...prev,
														awardNumber: e.target.value,
													}))
												}
												placeholder='VD: QD-001/2024'
												required
											/>
										</div>
										<div className='space-y-2'>
											<Label htmlFor='awardType'>Loại khen thưởng</Label>
											<Input
												id='awardType'
												value={formData.awardType}
												onChange={(e) =>
													setFormData((prev) => ({
														...prev,
														awardType: e.target.value,
													}))
												}
												placeholder='VD: Học sinh giỏi, Giấy khen'
												required
											/>
										</div>
										<div className='col-span-2 space-y-2'>
											<Label htmlFor='title'>Tên khen thưởng</Label>
											<Input
												id='title'
												value={formData.title}
												onChange={(e) =>
													setFormData((prev) => ({
														...prev,
														title: e.target.value,
													}))
												}
												placeholder='VD: Học sinh giỏi cấp thành phố'
												required
											/>
										</div>
										<div className='space-y-2'>
											<Label htmlFor='level'>Cấp độ</Label>
											<Input
												id='level'
												value={formData.level}
												onChange={(e) =>
													setFormData((prev) => ({
														...prev,
														level: e.target.value,
													}))
												}
												placeholder='VD: Quốc tịch, Tỉnh/TP, Trường'
												required
											/>
										</div>
										<div className='space-y-2'>
											<Label htmlFor='awardDate'>Ngày trao</Label>
											<Input
												id='awardDate'
												type='date'
												value={formData.awardDate}
												onChange={(e) =>
													setFormData((prev) => ({
														...prev,
														awardDate: e.target.value,
													}))
												}
												required
											/>
										</div>
										<div className='col-span-2 space-y-2'>
											<Label htmlFor='awardedBy'>Cơ quan trao</Label>
											<Input
												id='awardedBy'
												value={formData.awardedBy}
												onChange={(e) =>
													setFormData((prev) => ({
														...prev,
														awardedBy: e.target.value,
													}))
												}
												placeholder='VD: UBND TP.HCM'
												required
											/>
										</div>
										<div className='col-span-2 space-y-2'>
											<Label htmlFor='reason'>Lý do khen thưởng</Label>
											<Textarea
												id='reason'
												value={formData.reason}
												onChange={(e) =>
													setFormData((prev) => ({
														...prev,
														reason: e.target.value,
													}))
												}
												placeholder='Mô tả lý do được khen thưởng'
												required
											/>
										</div>
										<div className='space-y-2'>
											<Label htmlFor='academicYear'>Năm học</Label>
											<Input
												id='academicYear'
												value={formData.academicYear}
												onChange={(e) =>
													setFormData((prev) => ({
														...prev,
														academicYear: e.target.value,
													}))
												}
												placeholder='VD: 2023-2024'
											/>
										</div>
										<div className='space-y-2'>
											<Label htmlFor='semester'>Học kỳ</Label>
											<Input
												id='semester'
												value={formData.semester}
												onChange={(e) =>
													setFormData((prev) => ({
														...prev,
														semester: e.target.value,
													}))
												}
												placeholder='VD: I, II, Cả năm'
											/>
										</div>
										<div className='space-y-2'>
											<Label htmlFor='signedBy'>Người ký</Label>
											<Input
												id='signedBy'
												value={formData.signedBy}
												onChange={(e) =>
													setFormData((prev) => ({
														...prev,
														signedBy: e.target.value,
													}))
												}
												placeholder='VD: PGS.TS. Nguyễn Văn A'
												required
											/>
										</div>
										<div className='space-y-2'>
											<Label htmlFor='signerTitle'>Chức danh người ký</Label>
											<Input
												id='signerTitle'
												value={formData.signerTitle}
												onChange={(e) =>
													setFormData((prev) => ({
														...prev,
														signerTitle: e.target.value,
													}))
												}
												placeholder='VD: Hiệu trưởng, Chủ tịch UBND'
												required
											/>
										</div>
										<div className='space-y-2'>
											<Label htmlFor='validFrom'>Hiệu lực từ</Label>
											<Input
												id='validFrom'
												type='date'
												value={formData.validFrom}
												onChange={(e) =>
													setFormData((prev) => ({
														...prev,
														validFrom: e.target.value,
													}))
												}
												required
											/>
										</div>
										<div className='space-y-2'>
											<Label htmlFor='validTo'>Hiệu lực đến</Label>
											<Input
												id='validTo'
												type='date'
												value={formData.validTo}
												onChange={(e) =>
													setFormData((prev) => ({
														...prev,
														validTo: e.target.value,
													}))
												}
												placeholder='Để trống nếu vô thời hạn'
											/>
										</div>
										<div className='col-span-2 space-y-2'>
											<Label htmlFor='notes'>Ghi chú</Label>
											<Textarea
												id='notes'
												value={formData.notes}
												onChange={(e) =>
													setFormData((prev) => ({
														...prev,
														notes: e.target.value,
													}))
												}
												placeholder='Ghi chú thêm (nếu có)'
											/>
										</div>
									</div>
									<div className='flex justify-end gap-2'>
										<Button type='button' variant='destructive' onClick={() => setIsDialogOpen(false)}>
											Hủy
										</Button>
										<Button type='submit' variant={"add"}>
											Lưu
										</Button>
									</div>
								</form>
							</DialogContent>
						</Dialog>
					)}
					{/* {(canImport || canExport) && (
						<ImportExportActions
							onImport={canImport ? handleImport : undefined}
							onExport={canExport ? handleExport : undefined}
							templateData={generateAwardTemplate()}
							entityName='Khen thưởng'
							sampleFileName='khen_thuong'
						/>
					)} */}
				</div>
			</div>

			<Card>
				<CardHeader>
					<CardTitle className='flex items-center gap-2'>Danh sách khen thưởng</CardTitle>
					<div className='flex flex-wrap items-center gap-4 mt-4'>
						<div className='relative'>
							<Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4' />
							<Input placeholder='Tìm kiếm theo tên, CCCD, số khen thưởng...' value={searchQuery} onChange={(e) => handleFilterChange(setSearchQuery, e.target.value)} className='pl-10 w-80' />
						</div>
						<Select value={awardTypeFilter} onValueChange={(value) => handleFilterChange(setAwardTypeFilter, value)}>
							<SelectTrigger className='w-40'>
								<SelectValue placeholder='Loại khen thưởng' />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='all'>Tất cả loại</SelectItem>
								<SelectItem value='Học sinh giỏi'>Học sinh giỏi</SelectItem>
								<SelectItem value='Giấy khen'>Giấy khen</SelectItem>
								<SelectItem value='Giải thưởng cuộc thi'>Giải thưởng cuộc thi</SelectItem>
							</SelectContent>
						</Select>
						<Select value={levelFilter} onValueChange={(value) => handleFilterChange(setLevelFilter, value)}>
							<SelectTrigger className='w-40'>
								<SelectValue placeholder='Cấp độ' />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='all'>Tất cả cấp</SelectItem>
								<SelectItem value='Quốc tịch'>Quốc tịch</SelectItem>
								<SelectItem value='Thành phố'>Thành phố</SelectItem>
								<SelectItem value='Trường'>Trường</SelectItem>
							</SelectContent>
						</Select>
						<div className='flex items-center gap-2'>
							<Input type='date' placeholder='Từ ngày' value={dateFromFilter} onChange={(e) => handleFilterChange(setDateFromFilter, e.target.value)} className='w-36' />
							<span className='text-muted-foreground'>-</span>
							<Input type='date' placeholder='Đến ngày' value={dateToFilter} onChange={(e) => handleFilterChange(setDateToFilter, e.target.value)} className='w-36' />
						</div>
						{((awardTypeFilter && awardTypeFilter !== "all") || (levelFilter && levelFilter !== "all") || dateFromFilter || dateToFilter) && (
							<Button variant='outline' onClick={clearFilters}>
								Xóa bộ lọc
							</Button>
						)}
					</div>
				</CardHeader>

				<CardContent>
					<>
						<Select value={itemsPerPage.toString()} onValueChange={(value) => handleFilterChange(setItemsPerPage, value)}>
							<SelectTrigger className='w-20'>
								<SelectValue placeholder='Hình thức đào tạo' />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='10'>10</SelectItem>
								<SelectItem value='15'>15</SelectItem>
								<SelectItem value='20'>20</SelectItem>
								<SelectItem value='50'>50</SelectItem>
							</SelectContent>
						</Select>
						<div className='rounded-md border mt-2'>
							<Table className='min-w-[1200px]'>
								<TableHeader>
									<TableRow>
										<TableHead className='w-[150px]'>Họ tên</TableHead>
										<TableHead className='w-[130px]'>CCCD</TableHead>
										<TableHead className='w-[100px]'>Mã HS</TableHead>
										<TableHead className='w-[120px]'>Số khen thưởng</TableHead>
										<TableHead className='w-[200px]'>Tên khen thưởng</TableHead>
										<TableHead className='w-[120px]'>Loại</TableHead>
										<TableHead className='w-[100px]'>Cấp độ</TableHead>
										<TableHead className='w-[100px]'>Ngày trao</TableHead>
										<TableHead className='w-[150px]'>Cơ quan trao</TableHead>
										<TableHead className='w-[100px]'>Trạng thái</TableHead>
										<TableHead className='w-[100px]'>Thao tác</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{Array.isArray(awards) && awards.length > 0 ? (
										awards.map((award) => (
											<TableRow key={award.id}>
												<TableCell className='font-medium'>{award.studentName}</TableCell>
												<TableCell className='font-mono text-sm'>{award.cccd}</TableCell>
												<TableCell className='font-mono text-sm'>{award.studentId || "-"}</TableCell>
												<TableCell className='font-mono text-sm'>{award.awardNumber}</TableCell>
												<TableCell className='max-w-xs truncate' title={award.title}>
													{award.title}
												</TableCell>
												<TableCell>
													<Badge variant='outline' className='text-xs'>
														{award.awardType}
													</Badge>
												</TableCell>
												<TableCell>
													<Badge variant='secondary' className='text-xs'>
														{award.level}
													</Badge>
												</TableCell>
												<TableCell>{new Date(award.awardDate).toLocaleDateString("vi-VN")}</TableCell>
												<TableCell className='max-w-xs truncate' title={award.awardedBy}>
													{award.awardedBy}
												</TableCell>
												<TableCell>
													<Badge variant={getStatusVariant(award.status) as any} className='text-xs'>
														{getStatusLabel(award.status)}
													</Badge>
												</TableCell>
												<TableCell>
													<div className='flex items-center gap-2'>
														{canUpdate && (
															<Button variant='ghost' size='icon' onClick={() => handleEdit(award)} className='h-8 w-8'>
																<Edit className='w-4 h-4 text-yellow-600 hover:text-yellow-600' />
															</Button>
														)}
														{canDelete && (
															<Button variant='ghost' size='icon' onClick={() => setDeleteItemId(award.id)} className='h-8 w-8 text-destructive hover:text-destructive'>
																<Trash2 className='w-4 h-4' />
															</Button>
														)}
													</div>
												</TableCell>
											</TableRow>
										))
									) : (
										<TableRow>
											<TableCell colSpan={11} className='text-center py-8 text-muted-foreground'>
												{loading ? "Đang tải dữ liệu..." : "Không tìm thấy kết quả phù hợp"}
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</div>

						{/* Pagination */}
						{!loading && totalPages > 1 && <PaginationIndex setCurrentPage={setCurrentPage} currentPage={currentPage} itemsPerPage={itemsPerPage} totalCount={totalCount} totalPages={totalPages} />}
					</>
				</CardContent>
			</Card>

			{/* Delete modal */}
			<AlertDialog open={deleteItemId !== null} onOpenChange={() => setDeleteItemId(null)}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Xác nhận xóa</AlertDialogTitle>
						<AlertDialogDescription>Bạn có chắc chắn muốn xóa? Hành động này không thể hoàn tác.</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Hủy</AlertDialogCancel>
						<AlertDialogAction onClick={handleDelete} className='bg-destructive text-destructive-foreground hover:bg-destructive/90'>
							Xóa
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
